/**
 * Cloudflare Image Resizing / Transformations URL builder.
 *
 * Example:
 *   https://www.konstantino.com/cdn-cgi/image/width=400,format=auto/https://files.automatapp.com/konstantino/...
 *
 * Origin stays on REACT_APP_FILES_URL (GCS). Transforms run on the CF zone
 * (REACT_APP_IMAGE_CDN_URL), not via ImageKit `tr=` query params.
 */

function getImageCdnBase() {
  const raw =
    process.env.REACT_APP_IMAGE_CDN_URL ||
    process.env.REACT_APP_PUBLIC_URL ||
    "https://www.konstantino.com";
  const base = String(raw).replace(/\/$/, "");
  // Local CRA cannot serve /cdn-cgi/image — always use the public CF zone.
  if (/localhost|127\.0\.0\.1/i.test(base)) {
    return "https://www.konstantino.com";
  }
  return base;
}

function appendVersion(url, version) {
  if (version === undefined || version === null || version === "") return url;
  const join = url.includes("?") ? "&" : "?";
  return `${url}${join}v=${version}`;
}

/**
 * @param {string} originUrl Absolute origin image URL (GCS / files host)
 * @param {{
 *   width?: number,
 *   height?: number,
 *   quality?: number,
 *   fit?: string,
 *   format?: string,
 *   version?: string|number,
 * }} [opts]
 */
export function cfImage(originUrl, opts = {}) {
  const {
    width,
    height,
    quality = 85,
    fit = "scale-down",
    format = "auto",
    version,
  } = opts;

  if (!originUrl) return "";

  let source = String(originUrl).trim();
  // Allow passing a path that accidentally still has ImageKit tr=
  source = source.replace(/([?&])tr=[^&]*/g, "").replace(/\?&/, "?").replace(/[?&]$/, "");

  source = appendVersion(source, version);

  const params = [];
  if (width) params.push(`width=${width}`);
  if (height) params.push(`height=${height}`);
  if (fit) params.push(`fit=${fit}`);
  if (quality !== undefined && quality !== null) params.push(`quality=${quality}`);
  if (format) params.push(`format=${format}`);

  // No transform requested — return origin (still versioned).
  if (!params.length) return source;

  return `${getImageCdnBase()}/cdn-cgi/image/${params.join(",")}/${source}`;
}

/**
 * Build from files base + relative path.
 * mediaUrl(filesUrl, "products/x.jpg", { width: 800, version })
 * mediaUrl(fullUrl, { width: 800, version })
 */
export function mediaUrl(filesUrlOrFull, pathOrOpts, maybeOpts) {
  let origin;
  let opts;

  if (typeof pathOrOpts === "string") {
    const base = String(filesUrlOrFull || "").replace(/\/$/, "");
    const path = String(pathOrOpts || "").replace(/^\//, "");
    origin = path ? `${base}/${path}` : base;
    opts = maybeOpts || {};
  } else {
    origin = filesUrlOrFull;
    opts = pathOrOpts || {};
  }

  return cfImage(origin, opts);
}

export function mediaSrcSet(filesUrlOrFull, pathOrOpts, variants, maybeOpts) {
  let origin;
  let opts;
  let list;

  if (typeof pathOrOpts === "string") {
    const base = String(filesUrlOrFull || "").replace(/\/$/, "");
    const path = String(pathOrOpts || "").replace(/^\//, "");
    origin = `${base}/${path}`;
    list = variants;
    opts = maybeOpts || {};
  } else {
    origin = filesUrlOrFull;
    list = pathOrOpts;
    opts = variants || {};
  }

  return (list || [])
    .map(({ width, height, quality, fit, format, w }) => {
      const descriptor = w || width;
      const url = cfImage(origin, {
        ...opts,
        width,
        height,
        quality: quality ?? opts.quality,
        fit: fit ?? opts.fit,
        format: format ?? opts.format,
      });
      return `${url} ${descriptor}w`;
    })
    .join(", ");
}
